import { Default } from "../../components/Default/Default";
import { SYSTEM_COLORS } from "../../components/system.colors";
import { createDecimalFormatter, textFormatter } from "../../utils/formatter/formatter";
import { useDataIntegrationViewModel } from "../../viewModel/useDataIntegrationViewModel";

import { SectionTitle } from "../Products/ProductAnalytics/components/SectionTitle";

const EMPTY = "—";

const oneDecimalFmt = createDecimalFormatter({ maximumFractionDigits: 1 });
const dateTimeFmt = new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" });
const dateFmt = new Intl.DateTimeFormat("pt-BR", { dateStyle: "short" });

const hasValue = (value) => value !== null && value !== undefined && value !== "";

const HAS_TIMEZONE = /(Z|[+-]\d{2}:?\d{2})$/i;

// Datas com fuso (ex.: "2026-10-13T10:41:58.000Z") são convertidas para o horário local do navegador.
// Datas sem fuso ("YYYY-MM-DD" ou "YYYY-MM-DD HH:mm:ss") são montadas em horário local, evitando
// que "2025-01-02" vire 01/01 no Brasil por ser interpretada como UTC.
const parseLocalDate = (value) => {
  const text = String(value).trim();
  if (HAS_TIMEZONE.test(text)) return new Date(text);

  const [datePart, timePart = "00:00:00"] = text.split(/[ T]/);
  const [year, month, day] = datePart.split("-").map(Number);
  const [hours = 0, minutes = 0, seconds = 0] = timePart.split(":").map(Number);
  return new Date(year, month - 1, day, hours, minutes, Math.floor(seconds));
};

const formatWith = (fmt) => (value) => {
  if (!hasValue(value)) return EMPTY;
  const date = parseLocalDate(value);
  return Number.isNaN(date.getTime()) ? String(value) : fmt.format(date);
};

const formatDateTime = formatWith(dateTimeFmt);
const formatDate = formatWith(dateFmt);

const formatCompact = (value) => {
  if (!hasValue(value)) return EMPTY;
  const number = Number(value);
  if (number >= 1_000_000) return `${oneDecimalFmt.format(number / 1_000_000)}M`;
  if (number >= 1_000) return `${Math.round(number / 1_000)}k`;
  return String(number);
};

const NO_NEW_DATA_STATUS = "Sem dados novos";

const STATUS_STYLES = {
  "Concluído": { background: SYSTEM_COLORS.GREEN.FOREST_GREEN_10, color: SYSTEM_COLORS.GREEN.FOREST_GREEN },
  [NO_NEW_DATA_STATUS]: { background: SYSTEM_COLORS.RED.MISTY_ROSE, color: SYSTEM_COLORS.ORANGE.DEEP_ORANGE },
};

// Valores fixos pedidos para o card de tipo de integração; a API ainda não envia esses dados.
const INTEGRATION_TYPE_STATUS = "Estável";
const INTEGRATION_TYPE_DESCRIPTION = "API em tempo real para vendas e estoque; sFTP para cadastros históricos.";

const icon = (children, size = 18) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

const REFRESH_ICON = icon(<>
  <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
  <path d="M21 3v5h-5" />
  <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
  <path d="M8 16H3v5" />
</>);

const BOXES_ICON = icon(<>
  <path d="M2.97 12.92A2 2 0 0 0 2 14.63v3.24a2 2 0 0 0 .97 1.71l3 1.8a2 2 0 0 0 2.06 0L12 19v-5.5l-5-3-4.03 2.42Z" />
  <path d="m7 16.5-4.74-2.85" />
  <path d="m7 16.5 5-3" />
  <path d="M7 16.5v5.17" />
  <path d="M12 13.5V19l3.97 2.38a2 2 0 0 0 2.06 0l3-1.8a2 2 0 0 0 .97-1.71v-3.24a2 2 0 0 0-.97-1.71L17 10.5l-5 3Z" />
  <path d="m17 16.5-5-3" />
  <path d="m17 16.5 4.74-2.85" />
  <path d="M17 16.5v5.17" />
  <path d="M7.97 4.42A2 2 0 0 0 7 6.13v4.37l5 3 5-3V6.13a2 2 0 0 0-.97-1.71l-3-1.8a2 2 0 0 0-2.06 0l-3 1.8Z" />
  <path d="M12 8 7.26 5.15" />
  <path d="m12 8 4.74-2.85" />
  <path d="M12 13.5V8" />
</>);

const STORE_ICON = icon(<>
  <path d="M15 21v-5a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v5" />
  <path d="M17.774 10.31a1.12 1.12 0 0 0-1.549 0 2.5 2.5 0 0 1-3.451 0 1.12 1.12 0 0 0-1.548 0 2.5 2.5 0 0 1-3.452 0 1.12 1.12 0 0 0-1.549 0 2.5 2.5 0 0 1-3.77-3.248l2.889-4.184A2 2 0 0 1 7 2h10a2 2 0 0 1 1.653.873l2.895 4.192a2.5 2.5 0 0 1-3.774 3.244" />
  <path d="M4 10.95V19a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8.05" />
</>);

const LAYERS_ICON = icon(<>
  <path d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" />
  <path d="M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12" />
  <path d="M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17" />
</>, 16);

const CHECK_ICON = icon(<>
  <circle cx="12" cy="12" r="10" />
  <path d="m9 12 2 2 4-4" />
</>, 13);

const ALERT_ICON = icon(<>
  <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
  <path d="M12 9v4" />
  <path d="M12 17h.01" />
</>, 13);

const cardStyle = {
  background: SYSTEM_COLORS.WHITE.WHITE,
  border: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`,
  borderRadius: "18px",
  boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
};

const kpiLabelStyle = {
  fontSize: "0.7rem",
  fontWeight: 700,
  color: SYSTEM_COLORS.GRAY.DIM_GRAY,
  letterSpacing: "1.2px",
  textTransform: "uppercase",
};

const kpiValueStyle = {
  fontSize: "1.75rem",
  fontWeight: 800,
  color: SYSTEM_COLORS.BLACK.EERIE_BLACK,
  lineHeight: 1.15,
};

const thStyle = {
  padding: "0.7rem 0.6rem",
  fontSize: "0.9rem",
  fontWeight: 600,
  color: SYSTEM_COLORS.GRAY.MEDIUM_GRAY,
  textAlign: "left",
  whiteSpace: "nowrap",
  borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`,
};

const tdStyle = {
  padding: "0.65rem 0.6rem",
  fontSize: "0.9rem",
  color: SYSTEM_COLORS.BLACK.EERIE_BLACK,
  whiteSpace: "nowrap",
  borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`,
};

const emptyMessageStyle = {
  fontSize: "0.85rem",
  fontStyle: "italic",
  color: SYSTEM_COLORS.GRAY.GREY,
  textAlign: "center",
  padding: "1.5rem 0",
  margin: 0,
};

const CardTitle = ({ icon: titleIcon, children }) => (
  <h6 style={{ display: "flex", alignItems: "center", gap: "0.6rem", margin: "0 0 1rem", fontSize: "1.05rem", fontWeight: 600, color: SYSTEM_COLORS.BLACK.EERIE_BLACK }}>
    <span style={{ display: "flex", color: SYSTEM_COLORS.ORANGE.DEEP_ORANGE }}>{titleIcon}</span>
    {children}
  </h6>
);

const KpiDetail = ({ children }) => (
  <span style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.8rem", color: SYSTEM_COLORS.GRAY.DAVY_GRAY }}>
    <span style={{ inlineSize: "8px", blockSize: "8px", borderRadius: "50%", background: SYSTEM_COLORS.ORANGE.DEEP_ORANGE, flexShrink: 0 }} />
    {children}
  </span>
);

const KpiSummary = ({ summary }) => {
  const { lastPrediction, salesHistory, integrationType, dataVolume } = summary || {};

  const columns = [
    {
      label: "Última predição IA",
      value: formatDateTime(lastPrediction),
      detail: null,
    },
    {
      label: "Histórico de vendas",
      value: hasValue(salesHistory?.days) ? `${formatCompact(salesHistory.days)} dias` : EMPTY,
      detail: <KpiDetail>{`${formatDate(salesHistory?.startDate)} → ${formatDate(salesHistory?.endDate)}`}</KpiDetail>,
    },
    {
      label: "Tipo de integração",
      value: integrationType ?? EMPTY,
      detail: (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "0.6rem" }}>
          <span style={{
            padding: "0.2rem 0.5rem", borderRadius: "4px", fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.5px",
            textTransform: "uppercase", background: SYSTEM_COLORS.RED.MISTY_ROSE, color: SYSTEM_COLORS.ORANGE.DEEP_ORANGE,
          }}>
            {INTEGRATION_TYPE_STATUS}
          </span>
          <p style={{ margin: 0, fontSize: "0.8rem", color: SYSTEM_COLORS.GRAY.DAVY_GRAY, lineHeight: 1.5 }}>
            {INTEGRATION_TYPE_DESCRIPTION}
          </p>
        </div>
      ),
    },
    {
      label: "Volume de dados na base",
      value: dataVolume ?? EMPTY,
      detail: <KpiDetail>Registros integrados</KpiDetail>,
    },
  ];

  return (
    <section style={{ ...cardStyle, display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}>
      {columns.map((column, index) => (
        <div
          key={column.label}
          style={{
            display: "flex", flexDirection: "column", gap: "0.9rem", padding: "1.6rem 1.5rem 2rem",
            borderInlineStart: index > 0 ? `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}` : "none",
          }}
        >
          <span style={kpiLabelStyle}>{column.label}</span>
          <span style={kpiValueStyle}>{column.value}</span>
          {column.detail}
        </div>
      ))}
    </section>
  );
};

const SourcePill = ({ source }) => (
  <span style={{
    display: "inline-block", padding: "0.15rem 0.7rem", borderRadius: "999px",
    border: `1px solid ${SYSTEM_COLORS.GRAY.SILVER}`, fontSize: "0.78rem", fontWeight: 600,
    color: SYSTEM_COLORS.BLACK.EERIE_BLACK,
  }}>
    {source ?? EMPTY}
  </span>
);

const StatusPill = ({ status }) => {
  const label = status ?? NO_NEW_DATA_STATUS;
  const style = STATUS_STYLES[label];
  if (!style) return <span>{label}</span>;

  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "0.35rem", padding: "0.25rem 0.7rem",
      borderRadius: "999px", fontSize: "0.8rem", fontWeight: 600, background: style.background, color: style.color,
    }}>
      {label === NO_NEW_DATA_STATUS ? ALERT_ICON : CHECK_ICON}
      {label}
    </span>
  );
};

const IntegrationsTable = ({ integrations, emptyMessage }) => (
  <section style={{ ...cardStyle, padding: "1.6rem 1.8rem" }}>
    <CardTitle icon={REFRESH_ICON}>Dados Integrados</CardTitle>

    <div style={{ overflowX: "auto" }}>
      <table style={{ inlineSize: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={thStyle}>Integração</th>
            <th style={thStyle}>Origem</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Início</th>
            <th style={thStyle}>Fim</th>
            <th style={thStyle}>Duração</th>
          </tr>
        </thead>
        <tbody>
          {integrations.length === 0 ? (
            <tr>
              <td colSpan={6}>
                <p style={emptyMessageStyle}>{emptyMessage ?? "Nenhuma integração registrada até o momento."}</p>
              </td>
            </tr>
          ) : (
            integrations.map((integration) => (
              <tr
                key={integration.id ?? integration.name}
                onMouseEnter={(e) => { e.currentTarget.style.background = SYSTEM_COLORS.WHITE.WHITE_SMOKE; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                style={{ transition: "background 0.15s ease" }}
              >
                <td style={{ ...tdStyle, fontWeight: 600 }}>{integration.name ?? EMPTY}</td>
                <td style={tdStyle}><SourcePill source={integration.source} /></td>
                <td style={tdStyle}><StatusPill status={integration.status} /></td>
                <td style={tdStyle}>{formatDateTime(integration.startedAt)}</td>
                <td style={tdStyle}>{formatDateTime(integration.finishedAt)}</td>
                <td style={tdStyle}>{integration.duration ?? EMPTY}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </section>
);

const HierarchyCard = ({ title, titleIcon, levels, emptyMessage }) => (
  <section style={{ ...cardStyle, padding: "1.6rem 1.8rem" }}>
    <CardTitle icon={titleIcon}>{title}</CardTitle>

    {levels.length === 0 ? (
      <p style={emptyMessageStyle}>{emptyMessage ?? "Nenhum nível de hierarquia importado."}</p>
    ) : (
      <div style={{ display: "flex", flexDirection: "column", gap: "0.9rem" }}>
        {levels.map((level) => (
          <div
            key={level.level}
            style={{
              display: "flex", alignItems: "center", gap: "1rem", padding: "0.7rem 1rem",
              borderRadius: "12px", border: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`, background: SYSTEM_COLORS.WHITE.WHITE_SMOKE,
            }}
          >
            <span style={{
              display: "flex", alignItems: "center", justifyContent: "center", inlineSize: "36px", blockSize: "36px",
              borderRadius: "50%", background: SYSTEM_COLORS.RED.MISTY_ROSE, color: SYSTEM_COLORS.ORANGE.DEEP_ORANGE, flexShrink: 0,
            }}>
              {LAYERS_ICON}
            </span>
            <span style={{ fontSize: "0.92rem", fontWeight: 600, color: SYSTEM_COLORS.BLACK.EERIE_BLACK, minInlineSize: 0 }}>
              {textFormatter(level.label)}
            </span>
            <span style={{ marginInlineStart: "auto", fontSize: "0.95rem", fontWeight: 700, color: SYSTEM_COLORS.BLACK.EERIE_BLACK }}>
              {formatCompact(level.count)}
            </span>
          </div>
        ))}
      </div>
    )}
  </section>
);

export function DataIntegrationScreen() {
  const { ctx, uiState } = useDataIntegrationViewModel();

  const emptyMessage = uiState.isLoading
    ? "Carregando..."
    : uiState.hasError ? "Não foi possível carregar os dados de integração." : undefined;

  return (
    <Default>
      <div style={{ display: "flex", flexDirection: "column", inlineSize: "100%", padding: "2rem 1.5rem 3rem 1rem" }}>

        <SectionTitle
          title="INTEGRAÇÃO DE DADOS"
          description="Acompanhamento das cargas, hierarquias importadas e execução dos modelos preditivos da Aidron."
        />

        <div style={{ display: "flex", flexDirection: "column", gap: "1.8rem" }}>
          <KpiSummary summary={ctx.summary} />

          <IntegrationsTable integrations={ctx.integrations} emptyMessage={emptyMessage} />

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(22rem, 1fr))", gap: "1.2rem" }}>
            <HierarchyCard title="Hierarquia de Produto" titleIcon={BOXES_ICON} levels={ctx.productHierarchy} emptyMessage={emptyMessage} />
            <HierarchyCard title="Hierarquia de Lojas" titleIcon={STORE_ICON} levels={ctx.storeHierarchy} />
          </div>
        </div>

      </div>
    </Default>
  );
}
